import { useState } from "react";
import { authClient, authErrorMessage } from "@/lib/auth-client";

export const GMAIL_READONLY_SCOPE =
  "https://www.googleapis.com/auth/gmail.readonly";
export const GMAIL_SEND_SCOPE = "https://www.googleapis.com/auth/gmail.send";
export const GMAIL_MODIFY_SCOPE =
  "https://www.googleapis.com/auth/gmail.modify";

// Full set we ask for on every (re)connect attempt. Google merges the
// user's existing grants with these.
export const GMAIL_OAUTH_SCOPES = [
  GMAIL_READONLY_SCOPE,
  GMAIL_SEND_SCOPE,
  GMAIL_MODIFY_SCOPE,
];

export interface UseGmailReconnectResult {
  /**
   * Links (or re-links) the user's Google account via Better Auth with
   * the requested Gmail scopes. Google redirects back to the current
   * page afterwards so any persisted draft state is restored in place.
   */
  reconnect: (additionalScopes?: string[]) => Promise<void>;
  isPending: boolean;
  error: string | null;
  resetError: () => void;
}

/**
 * Shared hook for the Gmail OAuth (re)grant flow, used by the Inbox
 * page and the inline reply form on the Gmail message detail page.
 */
export function useGmailReconnect(): UseGmailReconnectResult {
  const { data: session } = authClient.useSession();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reconnect = async (
    additionalScopes: string[] = GMAIL_OAUTH_SCOPES,
  ): Promise<void> => {
    if (!session?.user) {
      setError("You need to be signed in to connect Gmail.");
      return;
    }
    setIsPending(true);
    setError(null);
    let redirecting = false;
    try {
      const callbackURL = window.location.href;
      const { data, error: linkError } = await authClient.linkSocial({
        provider: "google",
        scopes: additionalScopes,
        callbackURL,
        errorCallbackURL: callbackURL,
      });
      if (linkError) {
        const code = linkError.code ?? "";
        if (
          code === "PROVIDER_NOT_FOUND" ||
          linkError.status === 404 ||
          /provider not found|not configured/i.test(linkError.message ?? "")
        ) {
          setError(
            "Google sign-in is not configured on this MailCraft server yet. Ask an administrator to add Google OAuth credentials.",
          );
        } else {
          setError(authErrorMessage(linkError, "Could not start Gmail authorization."));
        }
        return;
      }
      const url = (data as { url?: string } | null)?.url;
      if (url) {
        redirecting = true;
        window.location.href = url;
        return;
      }
      setError("Google did not return a consent URL. Please try again.");
    } catch (err) {
      setError((err as Error)?.message ?? "Could not start Gmail authorization.");
    } finally {
      if (!redirecting) setIsPending(false);
    }
  };

  return {
    reconnect,
    isPending,
    error,
    resetError: () => setError(null),
  };
}
